import { createContext, useCallback, useRef } from 'react';

export type EventType = 'validate' | 'reset' | 'error';
export type Event = { type: EventType; payload?: { name: string; value: string } };

type FormGroupContextType = {
  register: (name: string, callback: (event: Event) => Promise<any> | any) => void;
  unregister: (name: string) => void;
  emit: (event: Event) => Promise<Record<string, any>>;
  collapsable: boolean;
  showValidationState: boolean;
  reviewMode: boolean;
  allowEdit: boolean;
};

const defaultFormGroupContext: FormGroupContextType = {
  register: () => {},
  unregister: () => {},
  emit: async () => ({}),
  collapsable: false,
  showValidationState: false,
  reviewMode: false,
  allowEdit: true
};

export const FormGroupContext = createContext<FormGroupContextType>(defaultFormGroupContext);

export const FormGroup = ({
  children,
  collapsable = false,
  showValidationState = false,
  reviewMode = false,
  allowEdit = true
}: {
  children: React.ReactNode;
  collapsable?: boolean;
  showValidationState?: boolean;
  reviewMode?: boolean;
  allowEdit?: boolean;
}) => {
  const registry = useRef<{ [key: string]: any }>({});

  const register = useCallback((name: string, callback: (event: Event) => void) => {
    const current = registry.current;

    if (current[name]) {
      throw new Error('component already registered');
    }

    current[name] = callback;
  }, []);

  const unregister = useCallback((name: string) => {
    delete registry.current[name];
  }, []);

  const emit = useCallback(async (event: Event) => {
    const results: Record<string, any> = {};

    const fields = Object.entries(registry.current);

    for (const [name, callback] of fields) {
      results[name] = await callback(event);
    }

    return results;
  }, []);

  return (
    <FormGroupContext.Provider
      value={{ register, unregister, emit, collapsable, showValidationState, reviewMode, allowEdit }}
    >
      {children}
    </FormGroupContext.Provider>
  );
};
